import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, mediaUrl } from "@/lib/api";
import { Upload, Loader2, Trash2 } from "lucide-react";

export const HeroManager = () => {
  const [hero, setHero] = useState(null);
  const [uploading, setUploading] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/hero").then((r) => setHero(r.data)).catch(() => setHero({}));
  }, []);

  const uploadVideo = async (e, key) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(key);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setHero((h) => ({ ...h, [key]: data.path }));
      toast.success("Video uploaded — remember to save");
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading("");
      e.target.value = "";
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/hero", {
        desktop_video: hero.desktop_video || null,
        mobile_video: hero.mobile_video || null,
        autoplay: hero.autoplay !== false,
        loop: hero.loop !== false,
        muted: hero.muted !== false,
        title: hero.title || "IGNOTA PARS",
        subtitle: hero.subtitle || "The Winter Collection",
      });
      toast.success("Hero video published");
    } catch (err) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!hero) return <p className="text-white/40 font-mono-label text-sm">Loading…</p>;

  const label = "font-mono-label text-[10px] uppercase tracking-luxe text-white/40 mb-2 block";
  const field = "w-full bg-transparent border border-white/20 px-4 py-3 text-white placeholder:text-white/30 focus:outline-none focus:border-white/60 text-sm";

  const VideoSlot = ({ k, title, hint }) => (
    <div className="border border-white/10 p-6">
      <div className="flex items-center justify-between mb-4">
        <span className="font-mono-label text-xs uppercase tracking-luxe text-white/70">{title}</span>
        {hero[k] && (
          <button data-testid={`hero-remove-${k}`} onClick={() => setHero((h) => ({ ...h, [k]: null }))} className="text-white/40 hover:text-white flex items-center gap-1 text-xs font-mono-label uppercase">
            <Trash2 size={13} /> Remove
          </button>
        )}
      </div>
      {hero[k] ? (
        <video src={mediaUrl(hero[k])} controls muted className="w-full aspect-video object-cover bg-black mb-4" data-testid={`hero-preview-${k}`} />
      ) : (
        <div className="w-full aspect-video bg-neutral-900 flex items-center justify-center text-white/30 font-mono-label text-xs uppercase tracking-luxe mb-4">
          No video · placeholder shown
        </div>
      )}
      <p className="text-white/40 text-xs mb-4">{hint}</p>
      <label className="inline-flex items-center gap-2 border border-white/25 px-5 py-3 cursor-pointer hover:border-white/60 font-mono-label text-xs uppercase tracking-luxe text-white/80">
        {uploading === k ? <Loader2 className="animate-spin" size={15} /> : <Upload size={15} strokeWidth={1.3} />}
        {hero[k] ? "Replace Video" : "Upload Video"}
        <input data-testid={`hero-upload-${k}`} type="file" accept="video/*" onChange={(e) => uploadVideo(e, k)} className="hidden" />
      </label>
    </div>
  );

  return (
    <div className="space-y-8" data-testid="hero-manager">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <VideoSlot k="desktop_video" title="Desktop Hero Video" hint="Full-screen video shown on the homepage (desktop & tablet)." />
        <VideoSlot k="mobile_video" title="Mobile Hero Video (optional)" hint="Vertical video shown on phones. Falls back to desktop if empty." />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 border border-white/10 p-6">
        <div>
          <label className={label}>Hero Title</label>
          <input data-testid="hero-title" value={hero.title || ""} onChange={(e) => setHero({ ...hero, title: e.target.value })} className={field} placeholder="IGNOTA PARS" />
        </div>
        <div>
          <label className={label}>Hero Subtitle</label>
          <input data-testid="hero-subtitle" value={hero.subtitle || ""} onChange={(e) => setHero({ ...hero, subtitle: e.target.value })} className={field} placeholder="The Winter Collection" />
        </div>
      </div>

      <div className="flex flex-wrap gap-8 border border-white/10 p-6">
        {[
          { k: "autoplay", label: "Autoplay" },
          { k: "loop", label: "Loop" },
          { k: "muted", label: "Muted (required for autoplay)" },
        ].map(({ k, label: l }) => (
          <label key={k} className="flex items-center gap-3 cursor-pointer">
            <input
              data-testid={`hero-${k}`}
              type="checkbox"
              checked={hero[k] !== false}
              onChange={(e) => setHero({ ...hero, [k]: e.target.checked })}
              className="accent-white w-4 h-4"
            />
            <span className="font-mono-label text-xs uppercase tracking-luxe text-white/70">{l}</span>
          </label>
        ))}
      </div>

      <button data-testid="hero-save" onClick={save} disabled={saving} className="px-10 py-4 bg-white text-black font-mono-label text-xs uppercase tracking-luxe hover-invert hover:bg-transparent hover:text-white border border-white disabled:opacity-50">
        {saving ? "Publishing…" : "Save & Publish"}
      </button>
    </div>
  );
};
